import React, { useState, useEffect } from 'react';
import { Mail, CheckCircle, Loader2 } from 'lucide-react';
import Button from './Button';
import { track } from '../services/track';

const NewsletterForm: React.FC = () => {
  const [email, setEmail] = useState('');
  const [isSubmitted, setIsSubmitted] = useState(false);
  const [isLoading, setIsLoading] = useState(false);

  useEffect(() => {
    const lastSubmit = localStorage.getItem('fpt_newsletter_submitted');
    if (lastSubmit === 'true') {
      setIsSubmitted(true);
    }
    track('waitlist_view');
  }, []);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!email.trim() || isLoading) return;

    setIsLoading(true);
    track('waitlist_submit', { email: email.length });

    // Simple simulation of waitlist capture
    // In production, this would POST to a Google Form or Mailchimp URL
    setTimeout(() => {
      setIsLoading(false);
      setIsSubmitted(true);
      localStorage.setItem('fpt_newsletter_submitted', 'true');
      track('waitlist_success');
    }, 1200);
  };

  if (isSubmitted) {
    return (
      <div className="bg-brand-light/40 border border-brand-green/20 rounded-2xl p-8 text-center animate-fade-in-up">
        <CheckCircle className="w-12 h-12 text-brand-green mx-auto mb-4" />
        <h3 className="text-xl font-heading font-bold text-brand-white mb-2">You're on the list!</h3>
        <p className="text-brand-grey2">Check your inbox for weekly career guides soon.</p>
      </div>
    );
  }

  return (
    <div className="bg-brand-dark border border-brand-border rounded-2xl p-8 relative overflow-hidden group">
      <div className="absolute top-0 right-0 -mr-8 -mt-8 w-24 h-24 bg-brand-cyan/5 rounded-full blur-2xl group-hover:bg-brand-cyan/10 transition-colors" />
      
      <div className="relative z-10 max-w-xl mx-auto text-center">
        <div className="inline-flex items-center gap-2 text-brand-cyan text-sm font-bold uppercase tracking-widest mb-4">
          <Mail className="w-4 h-4" />
          Stay Ahead
        </div>
        <h3 className="text-2xl md:text-3xl font-heading font-bold text-brand-white mb-4">
          Get Weekly Career Guides
        </h3>
        <p className="text-brand-grey2 mb-8">
          Join 5,000+ teens exploring high-demand future paths. One email, one career, every Sunday.
        </p>

        <form onSubmit={handleSubmit} className="flex flex-col sm:flex-row gap-3">
          <input
            type="email"
            required
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            placeholder="Enter your email..."
            className="flex-grow bg-brand-bg/50 border border-brand-border rounded-xl px-5 py-3 text-brand-white focus:outline-none focus:border-brand-cyan transition-colors"
          />
          <Button type="submit" disabled={isLoading} className="sm:w-auto">
            {isLoading ? <Loader2 className="w-5 h-5 animate-spin" /> : 'Join Waitlist'}
          </Button>
        </form>
        <p className="text-[10px] text-brand-grey2/50 mt-4">
          No spam. Unsubscribe anytime. By joining, you agree to our privacy policy.
        </p>
      </div>
    </div>
  );
};

export default NewsletterForm;
